import React from "react";
import PropTypes from "prop-types";

const SubscriptionModal = ({ show, onClose, subscriptionStatus, payLink, isLoading, message }) => {
    if (!show) return null;

    const {
        status,
        endDate,
        nomPlan,
        montant,
        periode,
        avantages = [],
        planName,
        description,
        gradient,
    } = subscriptionStatus || {};

    // Calcul du total à payer avec 1% de frais
    const totalAPayer = montant ? montant * 1.01 : 0;

    const getStatusInfo = () => {
        switch (status) {
            case "active":
                return {
                    title: "Abonnement Actif",
                    message: `Votre abonnement est valide jusqu’au ${new Date(endDate).toLocaleDateString('fr-FR')}.`,
                    description: "Merci pour votre confiance ! Profitez de toutes nos fonctionnalités premium.",
                    gradient: 'linear-gradient(135deg, #28a745, #20c997)',
                    buttonText: "Gérer l'abonnement"
                };
            case "expired":
                return {
                    title: "Abonnement Expiré",
                    message: `Votre abonnement a expiré le ${new Date(endDate).toLocaleDateString('fr-FR')}.`,
                    description: "Renouvelez dès maintenant pour retrouver l’accès à toutes les fonctionnalités premium.",
                    gradient: 'linear-gradient(135deg, #dc3545, #e91e63)',
                    buttonText: "Renouveler maintenant"
                };
            case "success":
                return {
                    title: "Abonnement Confirmé",
                    message: "Merci pour votre paiement !",
                    description: "Votre abonnement est désormais actif.",
                    gradient: 'linear-gradient(135deg, #28a745, #20c997)',
                    buttonText: "Gérer mon abonnement"
                };
            default:
                return {
                    title: nomPlan || planName || "Démarrez votre abonnement",
                    message: description || "Découvrez tous les avantages de notre offre premium.",
                    description: "Accédez à des fonctionnalités exclusives et boostez votre productivité dès aujourd’hui.",
                    gradient: gradient || 'linear-gradient(135deg, #667eea, #764ba2)',
                    buttonText: "Payer maintenant"
                };
        }
    };

    const statusInfo = getStatusInfo();

    const handlePayment = () => {
        if (payLink) {
            window.open(payLink, "_blank");
        }
        onClose();
    };

    const formatPrice = (amount) =>
        new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "XOF",
            minimumFractionDigits: 0,
        }).format(amount);

    return (
        <div style={styles.backdrop}>
            <div style={styles.modal}>
                {/* Header */}
                <div style={{ ...styles.header, background: statusInfo.gradient }}>
                    <div style={styles.headerContent}>
                        <h2 style={styles.title}>{statusInfo.title}</h2>
                        <button onClick={onClose} style={styles.closeBtn}>×</button>
                    </div>
                    <p style={styles.message}>{statusInfo.message}</p>
                </div>

                {/* Body */}
                <div style={styles.body}>
                    <div style={styles.infoBox}>
                        <span style={styles.icon}>ℹ️</span>
                        <p style={styles.infoText}>{statusInfo.description}</p>
                    </div>

                    <h3 style={styles.sectionTitle}>Détails de l’abonnement</h3>

                    <div style={styles.grid}>
                        <div>
                            <p style={styles.label}>Type de plan</p>
                            <p style={styles.value}>{nomPlan || planName || 'Non défini'}</p>
                        </div>
                        <div>
                            <p style={styles.label}>Total à payer maintenant</p>
                            <p style={{ fontWeight: 'bold', color: '#333' }}>
                                {montant ? formatPrice(totalAPayer) : "Non défini"} (incluant 1% de frais)
                            </p>
                        </div>
                    </div>

                    {avantages?.length > 0 && (
                        <>
                            <h3 style={styles.sectionTitle}>Fonctionnalités incluses</h3>
                            <ul style={styles.featureList}>
                                {avantages.map((feature, index) => (
                                    <li key={index} style={styles.featureItem}>✓ {feature}</li>
                                ))}
                            </ul>
                        </>
                    )}

                    {message && (
                        <div style={styles.alert}>
                            {message}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <button onClick={onClose} style={styles.cancelBtn}>
                        Plus tard
                    </button>
                    <button
                        onClick={handlePayment}
                        disabled={!payLink || isLoading}
                        style={{
                            ...styles.actionBtn,
                            background: payLink ? statusInfo.gradient : '#6c757d',
                            cursor: payLink ? 'pointer' : 'not-allowed'
                        }}
                    >
                        {payLink ? statusInfo.buttonText : "Payer maintenant"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    backdrop: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1050
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: '20px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
    },
    header: {
        color: 'white',
        padding: '20px',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px'
    },
    headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    title: {
        margin: 0
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        color: 'white',
        fontSize: '1.5rem',
        cursor: 'pointer'
    },
    message: {
        marginTop: '10px'
    },
    body: {
        padding: '20px'
    },
    infoBox: {
        backgroundColor: '#f8f9fa',
        padding: '15px',
        borderRadius: '10px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center'
    },
    icon: {
        marginRight: '10px'
    },
    infoText: {
        margin: 0
    },
    sectionTitle: {
        borderBottom: '2px solid #eee',
        paddingBottom: '10px',
        marginTop: '20px'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '15px',
        margin: '20px 0'
    },
    label: {
        color: '#6c757d',
        marginBottom: '5px'
    },
    value: {
        fontWeight: 'bold'
    },
    featureList: {
        paddingLeft: '20px',
        marginTop: '10px'
    },
    featureItem: {
        marginBottom: '10px'
    },
    alert: {
        background: "#fff3cd",
        color: "#856404",
        padding: "10px 15px",
        borderRadius: "8px",
        marginTop: "20px"
    },
    footer: {
        padding: '20px',
        borderTop: '1px solid #eee',
        display: 'flex',
        justifyContent: 'space-between',
        gap: '15px'
    },
    cancelBtn: {
        padding: '10px 20px',
        border: '1px solid #6c757d',
        borderRadius: '5px',
        background: 'none',
        cursor: 'pointer',
        flex: 1
    },
    actionBtn: {
        padding: '10px 20px',
        border: 'none',
        borderRadius: '5px',
        color: 'white',
        flex: 1
    }
};

SubscriptionModal.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func,
    subscriptionStatus: PropTypes.shape({
        status: PropTypes.string,
        endDate: PropTypes.string,
        nomPlan: PropTypes.string,
        montant: PropTypes.number,
        periode: PropTypes.string,
        planName: PropTypes.string,
        description: PropTypes.string,
        gradient: PropTypes.string,
        avantages: PropTypes.array
    }),
    payLink: PropTypes.string,
    isLoading: PropTypes.bool,
    message: PropTypes.string
};

export default SubscriptionModal;
